import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';

import { getTreatmentReportDetail } from '@/services/main/treatmentReport';
import useAppToast from '@/hooks/useAppToast';
import { getApiErrorMessage, DEFAULT_FETCH_ERROR_MESSAGE, isNotFoundError } from '@/utils/apiError';
import { useClinicContext } from './useClinicContext';
import { useClinicPortalServiceType } from './clinicPortalServiceType';

export const useTreatmentReportDetail = (
  patientId?: string,
  id?: string | number,
  options?: {
    enabled?: boolean;
    treatmentYearmonth?: number;
    filterByHeaderServiceType?: boolean;
    excludeExpiredCertificate?: boolean;
  }
) => {
  const { tenant, clinicNumber } = useClinicContext();
  const serviceType = useClinicPortalServiceType();
  const toast = useAppToast();

  const idStr = id != null ? String(id) : undefined;

  const { data, isLoading, isFetching, isError, error, refetch } = useQuery({
    queryKey: [
      'treatment-reports',
      'detail',
      tenant,
      clinicNumber,
      patientId,
      idStr,
      serviceType,
      options?.treatmentYearmonth,
    ],
    queryFn: () =>
      getTreatmentReportDetail(
        tenant! || '',
        clinicNumber! || '',
        patientId!,
        idStr!,
        serviceType,
        {
          treatmentYearmonth: options?.treatmentYearmonth,
          filterByHeaderServiceType: options?.filterByHeaderServiceType,
          excludeExpiredCertificate: options?.excludeExpiredCertificate,
        }
      ),
    enabled: (options?.enabled ?? true) && !!tenant && !!clinicNumber && !!patientId && !!idStr,
    staleTime: 0,
    placeholderData: previousData => previousData,
    meta: { showNotFoundToast: true },
  });

  useEffect(() => {
    if (isError && error && !isNotFoundError(error)) {
      toast.error(getApiErrorMessage(error, DEFAULT_FETCH_ERROR_MESSAGE));
    }
  }, [isError, error, toast]);

  return { data, isLoading: isLoading && !data, isFetching, isError, error, refetch };
};
